import type { FC } from 'react';
import { Fragment } from 'react';
import { Picture } from '@ui';
import icons from '@icons';
import { absoluteUrl } from '@constants/site';
import JsonLd from '@utils/jsonLd/JsonLd';
import { buildFaqPage } from '@utils/jsonLd/faq';
import type { CatalogFaqItem } from './selectItems';
import { toParagraphs } from './toParagraphs';

const {
  arrowIosLeft: { black: arrowBlackOutlined }
} = icons.Outline;

interface CatalogFaqProps {
  slug: string;
  items: CatalogFaqItem[];
}

const CatalogFaq: FC<CatalogFaqProps> = ({ slug, items }) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className='mt-6' data-testid='catalog-faq'>
      <JsonLd data={buildFaqPage(items, absoluteUrl(`/catalog/${slug}`))} />
      <h2 className='mb-2 text-xl font-bold sm:text-lg'>Вопросы и ответы</h2>
      <ul className='m-0 list-none p-0'>
        {items.map((item, index) => {
          const paragraphs = toParagraphs(item.answer);
          return (
            <Fragment key={`${index}-${item.title}`}>
              <li>
                <details className='group'>
                  <summary className='flex w-full cursor-pointer list-none items-center justify-between gap-3 py-3'>
                    <h3 className='m-0 text-sm font-medium leading-tight'>
                      {item.title}
                    </h3>
                    <Picture
                      icon={arrowBlackOutlined}
                      width={20}
                      height={20}
                      pictureClassName='shrink-0 -rotate-90 transition-transform group-open:rotate-90'
                    />
                  </summary>
                  <div className='pb-3 text-sm leading-tight'>
                    {paragraphs.map((paragraph, paragraphIndex) => (
                      <p key={paragraphIndex} className='mb-3 last:mb-0'>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </details>
              </li>
              {index !== items.length - 1 && (
                <hr className='m-0 h-px w-full border-0 bg-[var(--mid-grey)]' />
              )}
            </Fragment>
          );
        })}
      </ul>
    </section>
  );
};

export default CatalogFaq;
